import { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { Range } from 'react-range';
import { useTranslation } from 'react-i18next';
import {
  getGenderOptions,
  getSexualOrientationOptions,
  getRelationshipStatusOptions,
  getPreferencesOptions,
  getIsOriginalOptions,
  getIsNsfwOptions
} from '@/shared/constants/idolOptions';

/**
 * 偶像篩選表單 - 共用組件
 * 用於桌面版側邊欄和手機版 Modal
 * @param {string} uniqueId - 唯一識別碼，避免多個實例的 radio name 衝突
 */
const IdolFilterForm = ({ filters, onFilterChange, uniqueId = '' }) => {
  const { t } = useTranslation();
  const {
    gender,
    sexualOrientation,
    relationshipStatus,
    preferences,
    isOriginal,
    isNsfw,
    ageMin,
    ageMax
  } = filters;

  // 使用 local state 管理滑桿值以確保流暢拖曳
  const [ageValues, setAgeValues] = useState([ageMin, ageMax]);

  // 同步 URL 參數變化到 local state
  useEffect(() => {
    setAgeValues([ageMin, ageMax]);
  }, [ageMin, ageMax]);

  // 生成多語系選項
  const genderOptions = getGenderOptions(t);
  const sexualOrientationOptions = getSexualOrientationOptions(t);
  const relationshipStatusOptions = getRelationshipStatusOptions(t);
  const preferencesOptions = getPreferencesOptions(t);
  const isOriginalOptions = getIsOriginalOptions(t);
  const isNsfwOptions = getIsNsfwOptions(t);

  // react-select 值轉換
  const getGenderValue = () => {
    return genderOptions.filter(opt => gender.includes(opt.value));
  };

  const getSexualOrientationValue = () => {
    return sexualOrientationOptions.filter(opt => sexualOrientation.includes(opt.value));
  };

  const getRelationshipStatusValue = () => {
    return relationshipStatusOptions.filter(opt => relationshipStatus.includes(opt.value));
  };

  const getPreferencesValue = () => {
    return preferencesOptions.filter(opt => preferences.includes(opt.value));
  };

  return (
    <div className="d-flex flex-column gap-3">
      {/* 年齡 */}
      <div>
        <Form.Label className="fw-semibold">{t('age')}</Form.Label>
        <div className="px-2">
          <div className="d-flex justify-content-between mb-2">
            <small>{ageValues[0]}</small>
            <small>{ageValues[1]}</small>
          </div>
          <Range
            step={1}
            min={1}
            max={100}
            values={ageValues}
            onChange={(values) => {
              // 立即更新 local state（流暢拖曳）
              setAgeValues(values);
            }}
            onFinalChange={(values) => {
              // 拖曳結束後才更新 URL 參數（批次更新避免 stale state）
              onFilterChange({ age_min: values[0], age_max: values[1] });
            }}
            renderTrack={({ props, children }) => {
              const maxAge = 100;
              const leftPercent = ((ageValues[0] - 1) / (maxAge - 1)) * 100;
              const rightPercent = ((ageValues[1] - 1) / (maxAge - 1)) * 100;
              return (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: '6px',
                    width: '100%',
                    background: `linear-gradient(to right, #ddd ${leftPercent}%, #ff7bac ${leftPercent}%, #ff7bac ${rightPercent}%, #ddd ${rightPercent}%)`,
                    borderRadius: '3px',
                    alignSelf: 'center'
                  }}
                >
                  {children}
                </div>
              );
            }}
            renderThumb={({ props }) => {
              const { key, ...restProps } = props;
              return (
                <div
                  key={key}
                  {...restProps}
                  style={{
                    ...restProps.style,
                    height: '18px',
                    width: '18px',
                    backgroundColor: '#ff7bac',
                    borderRadius: '50%',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                />
              );
            }}
          />
        </div>
      </div>

      {/* 性別（多選） */}
      <div>
        <Form.Label className="fw-semibold">{t('gender')}</Form.Label>
        <Select
          isMulti
          options={genderOptions}
          value={getGenderValue()}
          onChange={(selected) => onFilterChange('gender', selected || [])}
          placeholder={t('select_option')}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      {/* 性向 */}
      <div>
        <Form.Label className="fw-semibold">{t('sexual_orientation')}</Form.Label>
        <Select
          isMulti
          options={sexualOrientationOptions}
          value={getSexualOrientationValue()}
          onChange={(selected) => onFilterChange('sexual_orientation', selected || [])}
          placeholder={t('select_option')}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      {/* 感情狀況 */}
      <div>
        <Form.Label className="fw-semibold">{t('relationship_status')}</Form.Label>
        <Select
          isMulti
          options={relationshipStatusOptions}
          value={getRelationshipStatusValue()}
          onChange={(selected) => onFilterChange('relationship_status', selected || [])}
          placeholder={t('select_option')}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      {/* 創作類型 */}
      <div>
        <Form.Label className="fw-semibold">{t('content_type')}</Form.Label>
        <Select
          isMulti
          options={preferencesOptions}
          value={getPreferencesValue()}
          onChange={(selected) => onFilterChange('preferences', selected || [])}
          placeholder={t('select_option')}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      {/* 是否原創 */}
      <div>
        <Form.Label className="fw-semibold">{t('idol_is_it_original')}</Form.Label>
        <div>
          {isOriginalOptions.map(opt => (
            <Form.Check
              key={opt.value}
              type="radio"
              name={`is_original${uniqueId}`}
              id={`is_original_${opt.value || 'all'}${uniqueId}`}
              label={opt.label}
              value={opt.value}
              checked={isOriginal === opt.value}
              onChange={(e) => onFilterChange('is_original', e.target.value)}
            />
          ))}
        </div>
      </div>

      {/* 是否工作場合不適 */}
      <div>
        <Form.Label className="fw-semibold">{t('idol_is_nsfw')}</Form.Label>
        <div>
          {isNsfwOptions.map(opt => (
            <Form.Check
              key={opt.value}
              type="radio"
              name={`is_nsfw${uniqueId}`}
              id={`is_nsfw_${opt.value || 'all'}${uniqueId}`}
              label={opt.label}
              value={opt.value}
              checked={isNsfw === opt.value}
              onChange={(e) => onFilterChange('is_nsfw', e.target.value)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default IdolFilterForm;
